import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from './useReducedMotion';

/**
 * useTypewriter — escribe una frase, la sostiene y la borra de atrás para
 * adelante, pasando a la siguiente. Sirve para sugerir ideas dentro del campo
 * de Lumière cuando todavía no se escribió nada.
 *
 * Con `prefers-reduced-motion` no anima: rota la frase completa cada
 * `hold + 1s`.
 */
export const useTypewriter = (phrases, {
  active = true,
  typeMs = 45,
  deleteMs = 22,
  holdMs = 2000,
  gapMs = 320,
} = {}) => {
  const reduced = useReducedMotion();
  const [text, setText] = useState('');
  const timer = useRef(null);
  const list = Array.isArray(phrases) ? phrases.filter(Boolean) : [];
  const key = list.join('|');

  useEffect(() => {
    clearTimeout(timer.current);
    if (!active || !list.length) {
      setText('');
      return undefined;
    }

    let phrase = 0;
    let char = 0;
    let deleting = false;
    let cancelled = false;

    const next = () => {
      if (cancelled) return;
      const current = list[phrase % list.length];

      if (reduced) {
        setText(current);
        phrase += 1;
        timer.current = setTimeout(next, holdMs + 1000);
        return;
      }

      if (!deleting) {
        char += 1;
        setText(current.slice(0, char));
        if (char >= current.length) {
          deleting = true;
          timer.current = setTimeout(next, holdMs);
        } else {
          timer.current = setTimeout(next, typeMs);
        }
        return;
      }

      char -= 1;
      setText(current.slice(0, Math.max(char, 0)));
      if (char <= 0) {
        deleting = false;
        phrase += 1;
        timer.current = setTimeout(next, gapMs);
      } else {
        timer.current = setTimeout(next, deleteMs);
      }
    };

    timer.current = setTimeout(next, gapMs);
    return () => { cancelled = true; clearTimeout(timer.current); };
    // Se depende de `key` (las frases serializadas) y no del array: así la
    // animación no se reinicia en cada render del componente padre.
  }, [key, active, reduced, typeMs, deleteMs, holdMs, gapMs]);

  return text;
};

export default useTypewriter;
